
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Slider from "react-slick";
import { FaUsers, FaDonate, FaBullhorn } from "react-icons/fa";
import { IoClose, IoEyeOutline, IoCheckmarkCircleOutline, IoTrash } from 'react-icons/io5';

// ✅ Slick Carousel styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { adminUpdateOrder, confirmPaymentApi, deleteOrder, getAllOrders, getAllRiders } from "../api/apiServices";
import { toast } from "react-toastify";

export default function OrdersPage() {
  const [allOrders, setAllOrders] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [riders, setRiders] = useState([]); // List of all riders
  const [updating, setUpdating] = useState(false);
  const [editFields, setEditFields] = useState({ to: "", newRiderId: "" });

 useEffect(() => {
     (async () => {
       try {
         const res = await getAllOrders();
         setAllOrders(res);
       } catch (err) {
         console.error(err);
       } finally {
         setLoading(false);
       }
     })();
   }, []);

   const ITEMS_PER_PAGE = 20;

   const orders = allOrders?.orders || [];

   const filteredOrders = orders.filter((o) => {
  const query = search.toLowerCase();

  return (
    o.orderId?.toLowerCase().includes(query) ||
    o.storeName?.toLowerCase().includes(query) ||
    o.vendorName?.toLowerCase().includes(query) ||
    o.shippingAddress?.city?.toLowerCase().includes(query)
  );
});

const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);

const paginatedOrders = filteredOrders.slice(
  (page - 1) * ITEMS_PER_PAGE,
  page * ITEMS_PER_PAGE
);


const totalOrders = orders.length;

const totalPayments = orders.reduce(
  (sum, o) => sum + (o.totalAmount || 0),
  0
);

const totalItemsAmount = orders.reduce((sum, o) => {
  const itemsTotal = o.items.reduce(
    (s, i) => s + i.pricePerUnit * i.quantity,
    0
  );
  return sum + itemsTotal;
}, 0);

const totalTripAmount = orders.reduce(
  (sum, o) => sum + (o.tripAmount || 0),
  0
);

const totalRevenue =
  totalItemsAmount * 0.1 + totalTripAmount * 0.05;


// Fetch riders on mount
useEffect(() => {
  const fetchRiders = async () => {
    try {
      const res = await getAllRiders();
      setRiders(res.data || []);
    } catch (err) {
      console.error("Error fetching riders", err);
    }
  };
  fetchRiders();
}, []);

useEffect(() => {
  if (selectedOrder) {
    setEditFields({
      to: selectedOrder.status,
      newRiderId: selectedOrder.riderId || ""
    });
  }
}, [selectedOrder]);

const handleAdminUpdate = async () => {
  try {
    setUpdating(true);
    // Call the new admin API endpoint
    await adminUpdateOrder(selectedOrder.orderId, editFields); 
    
    toast.success("Order updated successfully");
    
    // Refresh the list
    const res = await getAllOrders();
    setAllOrders(res);
    setSelectedOrder(null);
  } catch (err) {
    toast.error(err.response?.data?.error || "Update failed");
  } finally {
    setUpdating(false);
  }
};



  const handleDeleteOrder = async (orderId) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this order? This action cannot be undone."
  );

  if (!confirmed) return;

  try {
    setDeletingId(orderId);
    setSelectedOrder(null);

    await deleteOrder(orderId);

    setAllOrders((prev) => ({
      ...prev,
      orders: prev.orders.filter((o) => o.orderId !== orderId),
    }));

    toast.success("Order deleted successfully");
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete order");
  } finally {
    setDeletingId(null);
  }
};


const confirmPayment = async (orderId) => {
  const confirmed = window.confirm("Confirm this payment? This action cannot be undone.");

  if (!confirmed) return;

  try {
    await confirmPaymentApi(orderId);

    alert("Payment confirmed successfully!");
    setSelectedOrder(null);
    fetchOrders();
    return
  } catch (err) {
    alert(err.response?.data?.error || "Failed to confirm payment");
  }
};


  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* ✅ Hero Section */}
      <div className="relative h-[400px] bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center text-white">
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
            All Orders
          </h1>
          <p className="text-lg opacity-90">
            Here’s a quick summary of all orders on the platform
          </p>
        </div>
      </div>

      {/* ✅ Summary Section */}
      <motion.div
  initial={{ opacity: 0, y: 40 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7 }}
  className="max-w-6xl mx-auto px-4 mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
>
  <div className="p-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-2xl shadow-lg">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm opacity-80">Total Orders</p>
        <h3 className="text-3xl font-bold">{totalOrders}</h3>
      </div>
      <FaBullhorn size={36} />
    </div>
  </div>

  <div className="p-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl shadow-lg">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm opacity-80">Total Payments</p>
        <h3 className="text-3xl font-bold">
          ₦{totalPayments.toLocaleString()}
        </h3>
      </div>
      <FaDonate size={36} />
    </div>
  </div>

  <div className="p-6 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-2xl shadow-lg">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm opacity-80">Total Revenue</p>
        <h3 className="text-3xl font-bold">
          ₦{totalRevenue.toLocaleString()}
        </h3>
      </div>
      <FaUsers size={36} />
    </div>
  </div>
</motion.div>


      {/* 📦 Orders List */}
<div className="max-w-6xl mx-auto px-4 mt-16 mb-20">
  <h2 className="text-2xl font-bold mb-6">Orders</h2>

  <div className="mb-6">
  <input
    type="text"
    placeholder="Search by Order ID or Store name..."
    value={search}
    onChange={(e) => {
      setSearch(e.target.value);
      setPage(1);
    }}
    className="w-full md:w-1/2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
  />
</div>

  {/* Orders Grid */}
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {paginatedOrders.length === 0 && (
  <p className="col-span-full text-center text-gray-500">
    No orders found
  </p>
)}
    {paginatedOrders?.map((order) => (
      <div
        key={order._id}
        onClick={() => setSelectedOrder(order)}
        className="bg-white rounded-xl shadow-md p-5 hover:shadow-xl transition"
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="font-bold text-gray-800">{order.orderId}</p>
            <p className="text-sm text-gray-500">
              {order.storeName}
            </p>
          </div>

          <span
            className={`px-3 py-1 text-xs rounded-full font-semibold
              ${
                order.status === "pending"
                  ? "bg-yellow-100 text-yellow-700"
                  : order.status === "completed"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              }
            `}
          >
            {order.status}
          </span>
        </div>

        {/* Body */}
        <div className="text-sm text-gray-600 space-y-1">
          <p>
            📍 {order.shippingAddress?.city},{" "}
            {order.shippingAddress?.state}
          </p>
          <p>💳 {order.paymentMethod}</p>
          <p>
            📦 {order.items.length} item
            {order.items.length > 1 && "s"}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center mt-4">
          <p className="font-bold text-[#f97316]">
            ₦{order.totalAmount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400">
            {new Date(order.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>
    ))}
  </div>

  {/* Pagination */}
  <div className="flex justify-center items-center gap-4 mt-10">
    <button
      disabled={page === 1}
      onClick={() => setPage((p) => Math.max(p - 1, 1))}
      className={`px-4 py-2 rounded-lg font-semibold
        ${
          page === 1
            ? "bg-gray-200 text-gray-400"
            : "bg-indigo-600 text-white hover:bg-indigo-700"
        }
      `}
    >
      Previous
    </button>

    <span className="text-gray-600 font-medium">
      Page {page} of {totalPages}
    </span>

    <button
      disabled={page === totalPages}
      onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
      className={`px-4 py-2 rounded-lg font-semibold
        ${
          page === totalPages
            ? "bg-gray-200 text-gray-400"
            : "bg-indigo-600 text-white hover:bg-indigo-700"
        }
      `}
    >
      Next
    </button>
  </div>
</div>

{selectedOrder && (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl overflow-hidden">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 relative flex-shrink-0">
        <button
          onClick={() => setSelectedOrder(null)}
          className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl"
        >
          <IoClose size={28} />
        </button>
        
        <div className="flex justify-between items-start">
          <div>
            <p className="text-sm opacity-75">Order ID</p>
            <h3 className="text-2xl font-bold">{selectedOrder.orderId}</h3>
          </div>
          {/* Status with Green Check if Paid */}
          <div className="flex items-center gap-2">
            {selectedOrder.paymentStatus === "paid" && (
              <div className="flex items-center bg-green-500 text-white text-xs px-3 py-1 rounded-full">
                <IoCheckmarkCircleOutline size={18} className="mr-1" />
                PAID
              </div>
            )}
            <span className={`px-4 py-1.5 text-sm font-semibold rounded-full capitalize
              ${selectedOrder.status === "pending" ? "bg-yellow-400 text-yellow-900" :
                selectedOrder.status === "processing" ? "bg-blue-400 text-white" :
                selectedOrder.status === "confirmed" ? "bg-green-400 text-white" :
                selectedOrder.status === "delivered" ? "bg-emerald-500 text-white" : 
                "bg-gray-400 text-white"}`}
            >
              {selectedOrder.status}
            </span>
          </div>
        </div>
      </div>

      <div className="p-6 overflow-y-auto max-h-[75vh] space-y-8">
        
        {/* 1. Order Summary */}
        <div>
          <h4 className="font-semibold text-lg mb-3 text-gray-800">Order Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><b>Store:</b> {selectedOrder.storeName}</div>
            <div><b>Created:</b> {new Date(selectedOrder.createdAt).toLocaleString()}</div>
            <div><b>Updated:</b> {new Date(selectedOrder.updatedAt).toLocaleString()}</div>
            <div><b>Delivery Type:</b> {selectedOrder.deliveryType}</div>
            <div><b>Confirmed:</b> {selectedOrder.confirmed ? "Yes" : "No"}</div>
          </div>
        </div>

        {/* 2. Customer Details */}
        <div>
          <h4 className="font-semibold text-lg mb-3 text-gray-800">Customer Details</h4>
          <div className="bg-gray-50 p-4 rounded-xl text-sm space-y-2">
            <p><b>Buyer Phone:</b> {selectedOrder.buyerPhone}</p>
            <p><b>User ID:</b> {selectedOrder.userId}</p>
            {selectedOrder.shippingAddress && (
              <p>
                <b>Address:</b><br />
                {selectedOrder.shippingAddress.street}<br />
                {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.country}
                {selectedOrder.shippingAddress.zip && `, ${selectedOrder.shippingAddress.zip}`}
              </p>
            )}
          </div>
        </div>

        {/* 3. Vendor / Seller Details */}
        <div>
          <h4 className="font-semibold text-lg mb-3 text-gray-800">Vendor / Seller</h4>
          <div className="bg-gray-50 p-4 rounded-xl text-sm space-y-2">
            <p><b>Seller Name:</b> {selectedOrder.vendorName || "N/A"}</p>
            <p><b>Seller Phone:</b> {selectedOrder.vendorPhone || "N/A"}</p>
            <p><b>Seller ID:</b> {selectedOrder.sellerId}</p>
            {selectedOrder.sellerPhones?.length > 0 && (
              <div>
                <b>Registered Phones:</b>
                <ul className="mt-1 list-disc list-inside text-xs">
                  {selectedOrder.sellerPhones.map((sp, i) => (
                    <li key={i}>{sp.name} - {sp.phoneNumber}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* 4. Items */}
        <div>
          <h4 className="font-semibold text-lg mb-3 text-gray-800">Items ({selectedOrder.items.length})</h4>
          <div className="space-y-4">
            {selectedOrder.items.map((item, index) => (
              <div key={index} className="border rounded-xl p-4 bg-gray-50">
                <div className="flex gap-4">
                  {/* Product Images */}
                  {item.productId?.images?.length > 0 && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border">
                      <img
                        src={item.productId.images[0]}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="flex-1">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-xs text-gray-500">Store: {item.storeName}</p>
                    
                    <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                      <div>Qty: <b>{item.quantity}</b></div>
                      <div>Price/Unit: <b>₦{item.pricePerUnit}</b></div>
                      <div>Base Price: <b>₦{item.basePrice}</b></div>
                    </div>
                    
                    <p className="mt-2 font-bold text-right text-[#f97316]">
                      ₦{(item.pricePerUnit * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 5. Delivery & Rider Info */}
        <div>
          <h4 className="font-semibold text-lg mb-3 text-gray-800">Delivery Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl text-sm">
            <div><b>Delivery Fee:</b> ₦{selectedOrder.deliveryFee}</div>
            <div><b>Customer Charge:</b> ₦{selectedOrder.customerDeliveryCharge}</div>
            <div><b>Platform Subsidy:</b> ₦{selectedOrder.platformDeliverySubsidy}</div>
            <div><b>Distance:</b> {selectedOrder.deliveryDistanceKm.toFixed(2)} km</div>
            {selectedOrder.riderId && (
              <>
                <div><b>Rider ID:</b> {selectedOrder.riderId}</div>
                <div><b>Rider Phone:</b> {selectedOrder.riderPhone}</div>
              </>
            )}
          </div>
        </div>

        {/* 5.5 Management (Admin Override) */}
<div className="bg-indigo-50 p-5 rounded-2xl border border-indigo-100">
  <h4 className="font-bold text-indigo-900 mb-4 flex items-center gap-2">
    🛠️ Admin Management
  </h4>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Status Select */}
    <div>
      <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">Update Status</label>
      <select 
        value={editFields.to}
        onChange={(e) => setEditFields({ ...editFields, to: e.target.value })}
        className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
      >
        <option value="pending">Pending</option>
        <option value="processing">Processing</option>
        <option value="pickup">Pickup</option>
        <option value="shipped">Shipped</option>
        <option value="delivered">Delivered</option>
        <option value="confirmed">Confirmed</option>
        <option value="cancelled">Cancelled</option>
      </select>
    </div>

    {/* Rider Select */}
    <div>
      <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">Assign Rider</label>
      <select 
        value={editFields.newRiderId}
        onChange={(e) => setEditFields({ ...editFields, newRiderId: e.target.value })}
        className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
      >
        <option value="">Select a Rider</option>
        {riders.map(rider => (
          <option key={rider.userId} value={rider.userId}>
            {rider.firstName} {rider.lastName} ({rider.phoneNumber})
          </option>
        ))}
      </select>
    </div>
  </div>

  <button
    onClick={handleAdminUpdate}
    disabled={updating}
    className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 rounded-lg transition-all disabled:bg-gray-400"
  >
    {updating ? "Updating..." : "Save Management Changes"}
  </button>
</div>

        {/* 6. Payment Breakdown */}
        <div>
          <h4 className="font-semibold text-lg mb-3 text-gray-800">Payment Breakdown</h4>
          <div className="bg-gray-900 text-white p-5 rounded-2xl">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span> <span>₦{selectedOrder.subtotal}</span></div>
              <div className="flex justify-between"><span>Delivery Fee</span> <span>₦{selectedOrder.deliveryFee}</span></div>
              <hr className="border-gray-700" />
              <div className="flex justify-between font-bold text-lg">
                <span>Total Amount</span>
                <span>₦{selectedOrder.totalAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>Payment Method</span>
                <span>{selectedOrder.paymentMethod}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>Payment Status</span>
                <span className={`capitalize font-medium ${
                  selectedOrder.paymentStatus === "paid" ? "text-green-400" : 
                  selectedOrder.paymentStatus === "pending_verification" ? "text-amber-400" : "text-red-400"
                }`}>
                  {selectedOrder.paymentStatus || "pending"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions - Updated */}
      <div className="border-t bg-gray-50 p-6 flex justify-between items-center flex-shrink-0 gap-3 flex-wrap">
        
        {/* Delete Button */}
        <button
          onClick={() => handleDeleteOrder(selectedOrder.orderId)}
          disabled={deletingId === selectedOrder.orderId}
          className={`px-6 py-3 rounded-xl font-semibold text-white transition-all
            ${deletingId === selectedOrder.orderId ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
        >
          {deletingId === selectedOrder.orderId ? "Deleting..." : "🗑️ Delete Order"}
        </button>

        {/* View Proof Button */}
        {selectedOrder.paymentProof && (
          <button
            onClick={() => window.open(selectedOrder.paymentProof, '_blank')}
            className="px-6 py-3 rounded-xl font-semibold border border-gray-300 hover:bg-gray-100 flex items-center gap-2"
          >
            <IoEyeOutline size={20} />
            View Proof
          </button>
        )}

        {/* Confirm Payment Button - Only if not yet paid */}
        {selectedOrder.paymentStatus !== "paid" && (
          <button
            onClick={() => confirmPayment(selectedOrder._id)}
            className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all flex items-center gap-2"
          >
            <IoCheckmarkCircleOutline size={20} />
            Confirm Payment
          </button>
        )}

        {/* Close Button */}
        <button
          onClick={() => setSelectedOrder(null)}
          className="px-8 py-3 rounded-xl bg-gray-200 hover:bg-gray-300 font-semibold transition-all"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}



    </div>
  );
}
